import { ref } from 'vue'
import { v2 } from '@/services/api.js'

const SEARCH_DEBOUNCE_MS = 300
const MAX_RESULTS = 6
// Salesforce SOSL (which /accounts, /opportunities, and /contacts search all
// run under the hood) rejects search terms shorter than this, so searching on
// a 1-character query would just silently fail every time anyway — show
// guidance instead of quietly doing nothing.
const MIN_MENTION_QUERY_LENGTH = 2

/**
 * Finds an unclosed @mention or /command token immediately before the caret.
 *
 * @Mentions can open anywhere preceded by start-of-string or whitespace, same as
 * Slack/Linear/Notion. Slash commands only trigger when they're the very first
 * thing typed (mirrors Slack's slash commands, which can't appear mid-sentence).
 *
 * @param {string} value
 * @param {number} caret
 * @returns {{ type: 'mention' | 'command', start: number, end: number, query: string } | null}
 */
function findOpenTrigger(value, caret) {
  const beforeCaret = value.slice(0, caret)

  if (beforeCaret.startsWith('/') && !/\s/.test(beforeCaret)) {
    return { type: 'command', start: 0, end: caret, query: beforeCaret.slice(1) }
  }

  const at = beforeCaret.lastIndexOf('@')
  if (at === -1) return null

  const precedingChar = at === 0 ? null : beforeCaret[at - 1]
  if (precedingChar != null && !/\s/.test(precedingChar)) return null

  const token = beforeCaret.slice(at + 1)
  // A space after the @ means the mention was already closed further back;
  // this isn't the open trigger anymore.
  if (/\s/.test(token)) return null

  return { type: 'mention', start: at, end: caret, query: token }
}

async function searchMentionCandidates(query) {
  if (!query) return []

  const encoded = encodeURIComponent(query)

  const [accounts, opportunities, contacts] = await Promise.all([
    v2.get(`/accounts?filter[search]=${encoded}`).then((res) => res.data).catch(() => []),
    v2.get(`/opportunities?filter[search]=${encoded}`).then((res) => res.data).catch(() => []),
    v2.get(`/contacts?filter[search]=${encoded}`).then((res) => res.data).catch(() => []),
  ])

  const accountResults = (accounts ?? []).map((account) => ({
    key: `account-${account.id}`,
    recordType: 'Account',
    id: account.id,
    name: account.name,
    label: account.name,
    sublabel: 'Account',
  }))

  const opportunityResults = (opportunities ?? []).map((opportunity) => ({
    key: `opportunity-${opportunity.id}`,
    recordType: 'Opportunity',
    id: opportunity.id,
    name: opportunity.name,
    label: opportunity.name,
    sublabel: 'Opportunity',
  }))

  const contactResults = (contacts ?? []).map((contact) => ({
    key: `contact-${contact.id}`,
    recordType: 'Contact',
    id: contact.id,
    name: contact.name,
    label: contact.name,
    sublabel: [contact.title, contact.email].filter(Boolean).join(' · ') || 'Contact',
    // Carried through to the outgoing chat message (see resolveMentions in
    // AssistantChat.vue) so the model gets more than just a name + ID.
    email: contact.email,
    phone: contact.phone,
    title: contact.title,
  }))

  return [...accountResults, ...opportunityResults, ...contactResults].slice(0, MAX_RESULTS)
}

/**
 * Slash commands map 1:1 to a UI action the chat already knows how to render, so
 * this list only grows when a new action actually exists to jump to — no general
 * plugin registry to maintain speculatively.
 *
 * Each entry is one of two shapes, chosen by whether the lookup needs a fixed
 * set of choices or a whole separate composer:
 *
 * - `run`: opens a real bubble below the input — a structured ToolFormPrompt
 *   (see toolPrompts.js) for a dropdown/select field (e.g. /winloss's status),
 *   or a full composer (/boost's record picker, /task, /request) — for anything
 *   that isn't just typed text.
 * - `template`: everything else drops a fill-in-the-blank draft straight into
 *   the input instead of popping a form — the same shorthand the empty-state
 *   suggested-prompt chips use (see SUGGESTED_PROMPTS in AssistantChat.vue) —
 *   and lets the model figure out which tool to call from the resulting plain
 *   message, same as those chips do. A blank that's a Salesforce record (e.g.
 *   /health's account) is written as a bare "@" rather than "[account]": the
 *   chat input already has @mention search built in, so typing right after it
 *   looks the record up the same way a dedicated picker would, without needing
 *   one.
 *
 * Each entry's `tool` is the matching filename in agents/tools/*.js (every real
 * action tool maps to exactly one command). AssistantChat.vue uses it to let the
 * Capabilities popover (ToolsPopover.vue) run a tool's command by name — clicking
 * a capability there is just this same run/template dispatch, minus the actual
 * "/command" text ever having been typed.
 */
function buildSlashCommands({ openBoostPicker, openToolPrompt, openTaskComposer, openFieldLensRequestComposer }) {
  const entries = [
    {
      key: 'command-boost',
      command: '/boost',
      label: '/boost',
      sublabel: 'Open the Salesforce record picker for a Field Lens boost',
      tool: 'boost_fieldlens_request',
      run: () => openBoostPicker(),
    },
    {
      key: 'command-task',
      command: '/task',
      label: '/task',
      sublabel: 'Create a new Task',
      tool: 'create_task',
      run: () => openTaskComposer(),
    },
    {
      key: 'command-request',
      command: '/request',
      label: '/request',
      sublabel: 'Create a new Field Lens request',
      tool: 'create_fieldlens_request',
      run: () => openFieldLensRequestComposer(),
    },
    {
      key: 'command-wins',
      command: '/wins',
      label: '/wins',
      sublabel: 'Find similar recent wins by workload or competitor',
      tool: 'find_similar_wins',
      template: 'Find similar recent wins related to [workload or competitor].',
    },
    {
      key: 'command-winloss',
      command: '/winloss',
      label: '/winloss',
      sublabel: 'Get win/loss reasons against a competitor',
      tool: 'get_win_loss_reasons',
      run: () => openToolPrompt('winloss'),
    },
    {
      key: 'command-health',
      command: '/health',
      label: '/health',
      sublabel: 'Check an account for open support cases or escalations',
      tool: 'summarize_account_support_health',
      template: 'Check if @ has any open support cases before I meet with them.',
    },
    {
      key: 'command-docs',
      command: '/docs',
      label: '/docs',
      sublabel: 'Search internal how-to guides and SOPs',
      tool: 'search_intercom_articles',
      template: 'Search our internal docs for [what you are looking for].',
    },
    {
      key: 'command-compat',
      command: '/compat',
      label: '/compat',
      sublabel: 'Check the Nutanix compatibility matrix for a vendor/product',
      tool: 'check_compatibility_matrix',
      run: () => openToolPrompt('compat'),
    },
    {
      key: 'command-fieldlens',
      command: '/fieldlens',
      label: '/fieldlens',
      sublabel: 'Search the Field Lens request tracker',
      tool: 'search_field_lens_table',
      run: () => openToolPrompt('fieldlens'),
    },
    {
      key: 'command-feature',
      command: '/feature',
      label: '/feature',
      sublabel: 'Check Nutanix docs for an existing capability',
      tool: 'search_feature_support',
      template: 'Check if Nutanix supports [capability].',
    },
    {
      key: 'command-account',
      command: '/account',
      label: '/account',
      sublabel: 'Look up a Salesforce account by name or ID',
      tool: 'find_account',
      template: 'Look up the Salesforce account @.',
    },
    {
      key: 'command-contact',
      command: '/contact',
      label: '/contact',
      sublabel: 'Look up a Salesforce contact by name',
      tool: 'find_contact',
      // Free text, not "@": the mention picker's contact search only covers
      // Salesforce Contacts (customer-side people), not internal Nutanix
      // employees — a bracket lets the model resolve either case instead of
      // silently failing to find an internal name in the picker.
      template: 'Look up the Salesforce contact [contact name].',
    },
    {
      key: 'command-opportunity',
      command: '/opportunity',
      label: '/opportunity',
      sublabel: 'Look up a Salesforce opportunity by name',
      tool: 'find_opportunity',
      template: 'Look up the Salesforce opportunity [opportunity name].',
    },
  ]

  return entries
}

/**
 * Drives the @-mention / slash-command popover for the assistant chat textarea.
 * Owns trigger detection, debounced search, and keyboard navigation state; the
 * caller (AssistantChat.vue) owns the actual textarea/draft and is responsible
 * for splicing the selected result's text into place.
 *
 * @param {{ openBoostPicker: (payload?: object) => void, openToolPrompt: (key: string, payload?: object) => void, openTaskComposer: () => void, openFieldLensRequestComposer: () => void }} deps
 */
export default function useMentionAutocomplete({ openBoostPicker, openToolPrompt, openTaskComposer, openFieldLensRequestComposer }) {
  const trigger = ref(null)
  const results = ref([])
  const activeIndex = ref(0)
  const loading = ref(false)
  // Guidance shown in place of results/loading — e.g. "type more to search".
  const hint = ref(null)

  const slashCommands = buildSlashCommands({ openBoostPicker, openToolPrompt, openTaskComposer, openFieldLensRequestComposer })

  let debounceHandle = null
  let searchToken = 0

  function isOpen() {
    return trigger.value != null
  }

  function close() {
    trigger.value = null
    results.value = []
    activeIndex.value = 0
    loading.value = false
    hint.value = null

    if (debounceHandle != null) {
      clearTimeout(debounceHandle)
      debounceHandle = null
    }
  }

  function filterCommands(query) {
    const needle = query.trim().toLowerCase()
    if (!needle) return slashCommands

    return slashCommands.filter((entry) => entry.command.slice(1).toLowerCase().startsWith(needle))
  }

  function runMentionSearch(query) {
    if (debounceHandle != null) clearTimeout(debounceHandle)

    if (query.length < MIN_MENTION_QUERY_LENGTH) {
      results.value = []
      loading.value = false
      // No hint yet for an empty query — the popover just opened, give it a beat.
      hint.value = query
        ? `Keep typing... (${MIN_MENTION_QUERY_LENGTH}+ characters to search Accounts, Opportunities, and Contacts)`
        : null
      return
    }

    hint.value = null
    loading.value = true
    const token = ++searchToken

    debounceHandle = setTimeout(async () => {
      try {
        const found = await searchMentionCandidates(query)
        // Ignore stale responses from a search the user has already moved past.
        if (token !== searchToken) return
        results.value = found
      } finally {
        if (token === searchToken) loading.value = false
      }
    }, SEARCH_DEBOUNCE_MS)
  }

  /**
   * Called on every textarea input event. Detects whether the caret currently
   * sits inside an open @mention or /command token and updates the popover
   * accordingly, including closing it once the token is no longer open.
   *
   * @param {string} value Full textarea value.
   * @param {number} caret Caret position (`selectionStart`) within `value`.
   */
  function handleInput(value, caret) {
    const found = findOpenTrigger(value, caret)

    if (!found) {
      close()
      return
    }

    const triggerChanged = trigger.value?.type !== found.type || trigger.value?.start !== found.start
    trigger.value = found
    activeIndex.value = 0

    if (found.type === 'command') {
      results.value = filterCommands(found.query)
      loading.value = false
      hint.value = null
      return
    }

    // Re-running the debounce on every keystroke (not just when the trigger
    // first opens) is what makes the list narrow down live as the user types.
    if (triggerChanged) searchToken += 1
    runMentionSearch(found.query)
  }

  /**
   * Called on the textarea's keydown event while the popover may be open.
   * Returns true when the key was consumed (caller should preventDefault and
   * skip its own handling, e.g. Enter-to-send).
   *
   * @param {KeyboardEvent} event
   * @param {(result: object) => void} onSelect
   */
  function handleKeydown(event, onSelect) {
    if (!isOpen() || !results.value.length) return false

    if (event.key === 'ArrowDown') {
      activeIndex.value = (activeIndex.value + 1) % results.value.length
      return true
    }

    if (event.key === 'ArrowUp') {
      activeIndex.value = (activeIndex.value - 1 + results.value.length) % results.value.length
      return true
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      const selected = results.value[activeIndex.value]
      if (selected) onSelect(selected)
      return true
    }

    if (event.key === 'Escape') {
      close()
      return true
    }

    return false
  }

  return {
    activeIndex,
    close,
    handleInput,
    handleKeydown,
    hint,
    isOpen,
    loading,
    results,
    // Exposed so AssistantChat.vue can dispatch a command by its `tool` name
    // from the Capabilities popover, without the user ever typing "/command".
    slashCommands,
    trigger,
  }
}
